import React, { PureComponent } from 'react';
import styles from './AddLocationForm.module.less';
import { Checkbox, Col, Form, Input, Row, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import isEqual from 'react-fast-compare';
import { CUSTOM_LOCATION_ID } from '../../utils/constants';

export class AddLocationForm extends PureComponent {
  constructor(props) {
    super(props);
    const { formRef } = props;
    this.state = {};
    this.form = formRef;
  }

  form;

  componentDidUpdate(prevProps) {
    const { setFieldsValue } = this.form?.current;
    if (!isEqual(prevProps.location?.id, this.props.location?.id)) {
      setFieldsValue({
        location: this.props.location?.id,
        buildingNumber: this.props.location?.buildingNumber
      });
      if (this.props.location?.id !== CUSTOM_LOCATION_ID) {
        this.form?.current.validateFields(['location', 'buildingNumber'], { force: true });
      }
    }
  }

  onLocationSearch = query => {
    if (!query) {
      return;
    }

    const cityId = this.form?.current.getFieldValue('city');
    this.props.onLocationSearch(cityId, query);
  };
  onCityChange = cityId => {
    this.form?.current.resetFields(['location']);

    this.props.onLocationChange?.();
    this.props.onCityChange?.(cityId);
  };

  locationValidator = (locationId, callback) => {
    const { location, existingAddresses, t } = this.props;
    if (
      location &&
      existingAddresses.find(
        address =>
          address.building === location.building && address.street === location.street && address.area === location.area
      )
    ) {
      return callback(t('modal.addNewLocation.location.alreadyExists'));
    }
    callback();
  };

  getSearchLocationTitle(location) {
    const { building, area, formattedAddress } = location;
    if (building && area) {
      return `${building}, ${area}`;
    }
    if (building) {
      return `${building}`;
    }
    if (area && formattedAddress) {
      return `${formattedAddress}, ${area}`;
    }
    return formattedAddress;
  }

  render() {
    const { t, availableCities, availableLocations, locationSearchLoading, onLocationChange, location, defaultCityId } =
      this.props;
    const initialCity = availableCities?.length
      ? availableCities.find(city => city.id === defaultCityId)?.id || availableCities[0].id
      : '';
    return (
      <div className={styles.container}>
        <Form ref={this.form} layout="vertical" hideRequiredMark data-testid="addLocationForm">
          <Row>
            <Col xs={24}>
              <Form.Item
                name="city"
                label={t('modal.addNewLocation.city.label')}
                rules={[{ required: true }]}
                initialValue={initialCity}
              >
                <Select
                  size={'large'}
                  showSearch
                  optionFilterProp="children"
                  onChange={this.onCityChange}
                  data-testid="cities"
                >
                  {availableCities.map(city => (
                    <Select.Option value={city.id} key={`${city.id}`} data-testid="cityItem">
                      {city.nameLocalized}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <Form.Item
                name="location"
                label={t('modal.addNewLocation.location.label')}
                rules={[
                  { required: true, message: t('modal.addNewLocation.location.missing') },
                  {
                    validator: (_, value, callback) => this.locationValidator(value, callback)
                  }
                ]}
              >
                <Select
                  size={'large'}
                  allowClear
                  showSearch
                  defaultActiveFirstOption={false}
                  placeholder={t('modal.addNewLocation.location.placeholder')}
                  notFoundContent={locationSearchLoading ? <Spin /> : t('modal.addNewLocation.location.missing')}
                  optionFilterProp="children"
                  filterOption={false}
                  loading={locationSearchLoading}
                  onChange={onLocationChange}
                  onSearch={this.onLocationSearch}
                  data-testid="location"
                >
                  {availableLocations.map(location => (
                    <Select.Option value={location.id} key={`${location.id}`} data-testid="locationItem">
                      {this.getSearchLocationTitle(location)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {location ? (
            <Row>
              <Col xs={24}>
                <Form.Item
                  name="buildingNumber"
                  label={t('modal.addNewLocation.buildingNumber.label')}
                  rules={[{ required: true, message: t('modal.addNewLocation.buildingNumber.missing') }]}
                >
                  <Input
                    size={'large'}
                    placeholder={t('modal.addNewLocation.buildingNumber.placeholder')}
                    disabled={locationSearchLoading}
                    data-testid="buildingNumber"
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            ''
          )}
          <Row gutter={[32]}>
            <Col xs={24} md={12} lg={24}>
              <Form.Item name="note" label={t('modal.addNewLocation.note.label')}>
                <Input size={'large'} placeholder={t('modal.addNewLocation.note.placeholder')} data-testid="note" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={24} className={styles.nickname}>
              <Form.Item name="nickname" label={t('modal.addNewLocation.nickname.label')} rules={[{ required: true }]}>
                <Input
                  size={'large'}
                  placeholder={t('modal.addNewLocation.nickname.placeholder')}
                  data-testid="nickname"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <Form.Item
                name="setAsDefault"
                initialValue={true}
                className={styles.checkboxContainer}
                valuePropName="checked"
              >
                <Checkbox data-testid="setAsDefault">{t('modal.addNewLocation.setAsDefault')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }
}

AddLocationForm.propTypes = {
  t: PropTypes.func.isRequired,
  availableCities: PropTypes.array.isRequired,
  existingAddresses: PropTypes.array.isRequired,
  availableLocations: PropTypes.array.isRequired,
  locationSearchLoading: PropTypes.bool,
  onLocationSearch: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  onCityChange: PropTypes.func.isRequired,
  location: PropTypes.object,
  defaultCityId: PropTypes.number,
  formRef: PropTypes.object
};

export default withTranslation()(AddLocationForm);
